import { Box, Text, HStack, VStack, Image, Badge, Button, Collapsible } from '@chakra-ui/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown, FiChevronUp, FiExternalLink, FiEdit } from 'react-icons/fi';
import { MaleDogProposal } from '@/types/ai';
import { AIScoreBar, AIScoreCircle } from './AIScoreBar';
import { InbreedingIndicator, InbreedingBadge } from './InbreedingIndicator';

interface AIMatingProposalCardProps {
  /** 提案データ */
  proposal: MaleDogProposal;
  /** 順位（1から開始） */
  rank: number;
  /** メス犬のID（交配記録作成用） */
  femaleId: string;
  /** 詳細表示モード */
  detailed?: boolean;
}

/**
 * 順位に対応するバッジの色とアイコンを取得
 */
function getRankStyle(rank: number): { color: string; icon: string } {
  switch (rank) {
    case 1:
      return { color: 'yellow', icon: '🥇' };
    case 2:
      return { color: 'gray', icon: '🥈' };
    case 3:
      return { color: 'orange', icon: '🥉' };
    default:
      return { color: 'blue', icon: `#${rank}` };
  }
}

/**
 * AI交配提案カードコンポーネント
 */
export function AIMatingProposalCard({
  proposal,
  rank,
  femaleId,
  detailed = false,
}: AIMatingProposalCardProps) {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(detailed);
  const rankStyle = getRankStyle(rank);

  const handleViewDetail = () => {
    navigate(`/dogs/${proposal.maleId}`);
  };

  const handleCreateMatingRecord = () => {
    navigate(`/mating-records/new?maleId=${proposal.maleId}&femaleId=${femaleId}`);
  };

  return (
    <Box
      bg="white"
      borderRadius="lg"
      shadow="sm"
      border="1px solid"
      borderColor="gray.200"
      overflow="hidden"
      transition="all 0.2s"
      _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
    >
      {/* ヘッダー部分 */}
      <Box p={4}>
        <HStack justify="space-between" align="start" mb={3}>
          <HStack gap={3}>
            {/* 順位バッジ */}
            <Box
              fontSize="lg"
              fontWeight="bold"
              color={`${rankStyle.color}.600`}
            >
              {rankStyle.icon}
            </Box>

            {/* 犬の写真 */}
            <Box
              width="60px"
              height="60px"
              borderRadius="md"
              overflow="hidden"
              bg="gray.100"
              flexShrink={0}
            >
              {proposal.malePhotoUrl ? (
                <Image
                  src={proposal.malePhotoUrl}
                  alt={proposal.maleName}
                  width="100%"
                  height="100%"
                  objectFit="cover"
                />
              ) : (
                <Box
                  width="100%"
                  height="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color="gray.400"
                  fontSize="2xl"
                >
                  🐕
                </Box>
              )}
            </Box>

            {/* 犬の名前と基本情報 */}
            <VStack align="start" gap={1}>
              <Text fontWeight="bold" fontSize="lg">
                {proposal.maleName}
              </Text>
              <HStack gap={2}>
                <Badge colorPalette="blue" size="sm">
                  オス
                </Badge>
                <InbreedingBadge riskLevel={proposal.inbreedingRisk} />
              </HStack>
            </VStack>
          </HStack>

          {/* スコア表示 */}
          <AIScoreCircle score={proposal.overallScore} size="md" />
        </HStack>

        {/* スコア内訳バー */}
        <VStack gap={2} mb={3}>
          <AIScoreBar
            score={proposal.scoreBreakdown.geneticDiversity}
            label="遺伝的多様性"
          />
          <AIScoreBar
            score={proposal.scoreBreakdown.breedingHistory}
            label="繁殖実績"
          />
          <AIScoreBar
            score={proposal.scoreBreakdown.traitMatch}
            label="特徴マッチング"
          />
        </VStack>

        {/* 推奨理由（最初の1つ） */}
        {proposal.reasons.length > 0 && (
          <Box mb={3}>
            <Text fontSize="sm" color="gray.600">
              ✓ {proposal.reasons[0]}
            </Text>
          </Box>
        )}

        {/* 警告（ある場合） */}
        {proposal.warnings.length > 0 && (
          <Box
            p={2}
            bg="orange.50"
            borderRadius="md"
            mb={3}
          >
            <Text fontSize="sm" color="orange.700">
              ⚠️ {proposal.warnings[0]}
            </Text>
          </Box>
        )}

        {/* 展開ボタン */}
        <Button
          variant="ghost"
          size="sm"
          width="100%"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <HStack gap={1}>
            <Text fontSize="sm">詳細を{isExpanded ? '閉じる' : '見る'}</Text>
            {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
          </HStack>
        </Button>
      </Box>

      {/* 展開部分 */}
      <Collapsible.Root open={isExpanded}>
        <Collapsible.Content>
          <Box px={4} pb={4} pt={2} borderTop="1px solid" borderTopColor="gray.100">
            {/* 近親係数詳細 */}
            <Box mb={4}>
              <InbreedingIndicator
                coefficient={proposal.inbreedingCoefficient}
                riskLevel={proposal.inbreedingRisk}
              />
            </Box>

            {/* 推奨理由一覧 */}
            {proposal.reasons.length > 1 && (
              <Box mb={4}>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  推奨理由
                </Text>
                <VStack align="start" gap={1}>
                  {proposal.reasons.map((reason, index) => (
                    <Text key={index} fontSize="sm" color="gray.600">
                      • {reason}
                    </Text>
                  ))}
                </VStack>
              </Box>
            )}

            {/* 警告一覧 */}
            {proposal.warnings.length > 1 && (
              <Box mb={4}>
                <Text fontSize="sm" fontWeight="medium" mb={2} color="orange.600">
                  注意事項
                </Text>
                <VStack align="start" gap={1}>
                  {proposal.warnings.map((warning, index) => (
                    <Text key={index} fontSize="sm" color="orange.600">
                      ⚠️ {warning}
                    </Text>
                  ))}
                </VStack>
              </Box>
            )}

            {/* 期待される子犬の特徴 */}
            {proposal.expectedTraits && (
              <Box mb={4}>
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  期待される子犬の特徴
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {proposal.expectedTraits}
                </Text>
              </Box>
            )}

            {/* アクションボタン */}
            <HStack gap={2} mt={4}>
              <Button
                size="sm"
                variant="outline"
                onClick={handleViewDetail}
              >
                <HStack gap={1}>
                  <FiExternalLink />
                  <Text>詳細を見る</Text>
                </HStack>
              </Button>
              <Button
                size="sm"
                colorPalette="blue"
                onClick={handleCreateMatingRecord}
              >
                <HStack gap={1}>
                  <FiEdit />
                  <Text>交配記録を作成</Text>
                </HStack>
              </Button>
            </HStack>
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
}
